import React from 'react';
import { useTranslation } from 'react-i18next';
import useClasses from '../../hooks/useClasses';

import './style.scss';

interface Props<T extends Record<string, string>> {
  options: T
  selected?: keyof T
  onChange?: (selected: keyof T) => void
  title?: string
}

const KEYCODE_LEFT_ARROW_KEY = 37;
const KEYCODE_RIGHT_ARROW_KEY = 39;
const KEYCODE_UP_ARROW_KEY = 38;
const KEYCODE_DOWN_ARROW_KEY = 40;

const SegmentedControl = <T extends Record<string, string>>({
  options,
  selected,
  onChange,
  title,
}: Props<T>): JSX.Element => {
  const { t } = useTranslation();
  const { buildClass } = useClasses('SegmentedControl');
  const ref = React.useRef<HTMLDivElement>(null);
  const optionKeys = Object.keys(options);
  const currentTabIndex = optionKeys.findIndex((option) => option === selected);

  function selectNextTab() {
    if (currentTabIndex < 0 || ref.current == null) {
      return;
    }

    const nextTabKey = optionKeys[currentTabIndex + 1];

    if (nextTabKey == null && ref.current == null) {
      return;
    }

    const nextTab: HTMLElement = ref.current.querySelector(`#segmented-control_${nextTabKey}`);

    if (!nextTab) {
      return;
    }

    nextTab.focus();

    if (onChange) {
      onChange(nextTabKey);
    }
  }

  function selectPreviousTab() {
    if (currentTabIndex < 1 || ref.current == null) {
      return;
    }

    const previousTabKey = optionKeys[currentTabIndex - 1];

    if (previousTabKey == null) {
      return;
    }

    const previousTab: HTMLElement = ref.current.querySelector(`#segmented-control_${previousTabKey}`);

    if (!previousTab) {
      return;
    }

    previousTab.focus();

    if (onChange) {
      onChange(previousTabKey);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    switch (e.keyCode) {
      case KEYCODE_UP_ARROW_KEY:
      case KEYCODE_LEFT_ARROW_KEY:
        e.preventDefault();
        selectPreviousTab();
        break;
      case KEYCODE_DOWN_ARROW_KEY:
      case KEYCODE_RIGHT_ARROW_KEY:
        e.preventDefault();
        selectNextTab();
        break;
      default:
        break;
    }
  }

  return (
    <div className={buildClass()} ref={ref}>
      <h2 className="sr-only">
        {title || t('aria_labels.segmented_control_title')}
      </h2>
      <ul className={buildClass('list')} role="tablist">
        {
          Object.keys(options).map((optionKey) => {
            const isSelected = selected === optionKey;

            return (
              <li
                className={buildClass('list-item', {
                  selected: isSelected,
                })}
                key={optionKey}
                role="presentation"
              >
                <button
                  type="button"
                  className={buildClass('button')}
                  id={`segmented-control_${optionKey}`}
                  role="tab"
                  aria-selected={isSelected}
                  tabIndex={isSelected ? 0 : -1}
                  onKeyDown={handleKeyDown}
                  onClick={(e) => {
                    if (onChange) {
                      e.preventDefault();
                      onChange(optionKey);
                    }
                  }}
                >
                  {options[optionKey]}
                </button>
              </li>
            );
          })
        }
      </ul>
    </div>
  );
};

export default SegmentedControl;
